import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import { apiRequest } from '../api/client.js';
import type { NamePaint } from '../api/types.js';
import { usePreferences } from './use-preferences.js';

/**
 * Name paints for the viewers currently on screen (§56, §65).
 *
 * ============================ WHY IT WORKS THIS WAY ============================
 * 7TV has no batch endpoint for paints: one request per distinct viewer. So
 * the Console asks for the ids it has, in batches, and remembers the answer —
 * including the negative one, because most viewers wear no paint and asking
 * again each time they speak would be a request per message.
 *
 * A viewer with no paint, an unreachable 7TV, or the preference off are all the
 * same outcome: no entry, and the name renders exactly as it does today.
 * =========================================================================
 */

type PaintsValue = { paintFor: (platformUserId: string | undefined) => NamePaint | undefined };

const PaintsContext = createContext<PaintsValue>({ paintFor: () => undefined });

/** Bounded, so a long session cannot grow this without limit. */
const MAX_REMEMBERED = 500;

export function PaintsProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const { preferences } = usePreferences();
  const enabled = preferences.sevenTvPaints;

  const [paints, setPaints] = useState<Record<string, NamePaint>>({});
  /** Ids already asked about, whatever the answer, so none is asked twice. */
  const asked = useRef<Set<string>>(new Set());
  const [pending, setPending] = useState<string[]>([]);

  useEffect(() => {
    if (!enabled) {
      asked.current = new Set();
      setPaints({});
      setPending([]);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || pending.length === 0) return undefined;

    const controller = new AbortController();
    const batch = pending.slice(0, 50);

    void (async () => {
      try {
        const data = await apiRequest<{ paints: Record<string, NamePaint> }>(
          `/paints?platform=twitch&ids=${batch.map(encodeURIComponent).join(',')}`,
          { signal: controller.signal },
        );

        setPaints((current) => {
          const merged = Object.entries({ ...current, ...data.paints });

          /*
           * Rebuilt from the newest entries rather than deleting from the old
           * object: insertion order is what makes "oldest" meaningful here,
           * and `slice` states the bound in one place.
           */
          return Object.fromEntries(merged.slice(-MAX_REMEMBERED));
        });
      } catch {
        // Paints are decoration; a failure leaves names plain.
      } finally {
        setPending((current) => current.filter((id) => !batch.includes(id)));
      }
    })();

    return () => {
      controller.abort();
    };
  }, [enabled, pending]);

  const value = useMemo<PaintsValue>(
    () => ({
      paintFor: (platformUserId) => {
        if (!enabled || platformUserId === undefined || platformUserId === '') return undefined;

        if (!asked.current.has(platformUserId)) {
          asked.current.add(platformUserId);
          // Queued rather than fetched here: this runs during render, and a
          // request per row would be one per message.
          queueMicrotask(() => {
            setPending((current) =>
              current.includes(platformUserId) ? current : [...current, platformUserId],
            );
          });
        }

        return paints[platformUserId];
      },
    }),
    [enabled, paints],
  );

  return <PaintsContext.Provider value={value}>{children}</PaintsContext.Provider>;
}

export function usePaints(): PaintsValue {
  return useContext(PaintsContext);
}
